import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Heart } from "lucide-react";
import { CONTACT } from "@/config/contact";
import { useFavorites } from "@/context/FavoritesContext";

const nav = [
  { to: "/", label: "Início" },
  { to: "/comprar-casa-no-porto", label: "Comprar" },
  { to: "/vender-casa-no-porto", label: "Vender" },
  { to: "/zonas", label: "Zonas" },
  { to: "/imoveis", label: "Imóveis" },
  { to: "/blog", label: "Blog" },
  { to: "/sobre", label: "Sobre a Zélia" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useFavorites();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="relative w-full"
    >
      <div
        className={`sticky top-0 z-40 w-full backdrop-blur-xl transition-colors duration-300 ${
          scrolled ? "bg-white/90 border-b border-stone-200/70" : "bg-white/70"
        }`}
      >
        <div className="container-cnp flex items-center justify-between py-3">
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1).replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm transition-colors ${
                  isActive ? "text-[#103C4A]" : "text-stone-700 hover:text-[#103C4A]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 ml-auto">
          <Link to="/favoritos" data-testid="header-favoritos" className="btn-ghost relative" aria-label="Favoritos">
            <Heart className={`h-4 w-4 ${count > 0 ? "fill-[#103C4A] text-[#103C4A]" : ""}`} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#103C4A] px-1 text-[10px] font-medium text-white">{count}</span>
            )}
          </Link>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="header-phone-link"
            className="btn-ghost inline-flex items-center gap-1.5 text-xs whitespace-nowrap shrink-0"
          >
            <Phone className="h-3 w-3" /> {CONTACT.phone}
          </a>
          <Link
            to="/avaliacao-gratuita"
            data-testid="header-cta-valuation"
            className="btn-primary"
          >
            Avaliação gratuita
          </Link>
        </div>

        <button
          type="button"
          data-testid="header-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center rounded-full border border-stone-200 bg-white p-2 text-stone-800"
          aria-label="Abrir menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      </div>

      <div className="flex items-center justify-center py-2 sm:py-3 bg-white">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="group flex items-center"
        >
          <img src="/logo.png" alt="casa no porto" className="h-12 sm:h-20 md:h-32 lg:h-40 w-auto max-w-[92vw] object-contain shrink-0" />
        </Link>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-stone-200 bg-white">
          <div className="container-cnp py-4 flex flex-col gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1).replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-base ${
                    isActive ? "bg-stone-100 text-[#103C4A]" : "text-stone-800 hover:bg-stone-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/avaliacao-gratuita"
              data-testid="mobile-cta-valuation"
              className="btn-primary mt-3"
            >
              Avaliação gratuita
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
